import React from 'react';
import {
  Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Typography, Grid, Box, Button,
  useMediaQuery
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { useTheme } from '@mui/material/styles';
import { formatToLocalDateTime } from '../../../utils/dateFormatter';

export default function InventoryTransactionsReportDataGrid({ data = null }) {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  // ✅ CSV Export Handler
  const handleDownloadCSV = () => {
    if (!data || data.length === 0) return;

    const headers = [
      "Transaction Date",
      "Reference Code",
      "Transaction Type",
      "Item Category",
      "Item Name",
      "Flow",
      "Qty",
      "UoM",
      "Total Cost",
      "Total Price",
      "Supplier",
      "Procedure",
      "Remarks",
      "Created By"
    ];

    // Escape function for CSV fields
    const escapeCSV = (value) => {
      if (value === null || value === undefined) return "";
      const str = String(value);
      // If contains comma, quote, or newline → wrap in quotes
      if (/[",\n]/.test(str)) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    const rows = data.map((row) => [
      row.transactionDate,
      row.referenceCode,
      row.transactionType,
      row.itemCategory,
      escapeCSV(row.itemName),
      row.flow,
      row.qty,
      row.uom,
      row.totalCost,
      row.totalPrice,
      escapeCSV(row.supplier),
      row.procedure,
      escapeCSV(row.remarks),
      escapeCSV(row.createdBy)
    ]);

    const csvContent =
      [headers, ...rows]
        .map((e) => e.join(","))
        .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "inventory_transactions_report.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Box>

      {/* ✅ Download Button */}
      <Box sx={{ mb: 2 }}>
        <Button variant="outlined" fullWidth={!isDesktop} color="primary" onClick={handleDownloadCSV}>
          <DownloadIcon /> Download CSV
        </Button>
      </Box>

      {/* ✅ Table with Sticky Header */}
      <TableContainer
        component={Paper}
        sx={{
          border: '1px solid lightgray',
          flex: 1,              // fill remaining space
          maxHeight: 450,    // respect Dialog’s maxHeight
          overflow: 'auto'      // scroll inside modal
        }}
      >
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {[
                "Transaction Date",
                "Reference Code",
                "Transaction Type",
                "Item Category",
                "Item Name",
                "Flow",
                "Qty",
                "UoM",
                "Total Cost",
                "Total Price",
                "Supplier",
                "Procedure",
                "Remarks",
                "Created By"
              ].map((header, idx) => (
                <TableCell
                  key={idx}
                  sx={{
                    verticalAlign: 'top',
                    lineHeight: 1,
                    py: 2,
                    backgroundColor: 'background.paper',
                    fontWeight: 'bold',
                    whiteSpace: 'nowrap'
                  }}
                  align={['Qty', 'Total Cost', 'Total Price'].includes(header) ? 'right' : 'center'}
                >
                  {header}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((row, idx) => (
              <TableRow key={idx}>
                <TableCell sx={{ verticalAlign: 'top', lineHeight: 1, py: 0.5, whiteSpace: 'nowrap' }} align='left'>{row.transactionDate}</TableCell>
                <TableCell sx={{ verticalAlign: 'top', lineHeight: 1, py: 0.5, whiteSpace: 'nowrap' }} align='center'>{row.referenceCode}</TableCell>
                <TableCell sx={{ verticalAlign: 'top', lineHeight: 1, py: 0.5 }} align='center'>{row.transactionType}</TableCell>
                <TableCell sx={{ verticalAlign: 'top', lineHeight: 1, py: 0.5 }} align='center'>{row.itemCategory}</TableCell>
                <TableCell sx={{ verticalAlign: 'top', lineHeight: 1, py: 0.5 }} align='center'>{row.itemName}</TableCell>
                <TableCell sx={{ verticalAlign: 'top', lineHeight: 1, py: 0.5 }} align='center'>{row.flow}</TableCell>
                <TableCell sx={{ verticalAlign: 'top', lineHeight: 1, py: 0.5 }} align='right'>{row.qty}</TableCell>
                <TableCell sx={{ verticalAlign: 'top', lineHeight: 1, py: 0.5 }} align='center'>{row.uom}</TableCell>
                <TableCell sx={{ verticalAlign: 'top', lineHeight: 1, py: 0.5 }} align='right'>{row.totalCost}</TableCell>
                <TableCell sx={{ verticalAlign: 'top', lineHeight: 1, py: 0.5 }} align='right'>{row.totalPrice}</TableCell>
                <TableCell sx={{ verticalAlign: 'top', lineHeight: 1, py: 0.5 }} align='center'>{row.supplier}</TableCell>
                <TableCell sx={{ verticalAlign: 'top', lineHeight: 1, py: 0.5 }} align='center'>{row.procedure}</TableCell>
                <TableCell sx={{ verticalAlign: 'top', lineHeight: 1, py: 0.5 }} align='left'>{row.remarks}</TableCell>
                <TableCell sx={{ verticalAlign: 'top', lineHeight: 1, py: 0.5 }} align='center'>{row.createdBy}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
